import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Play, Link, Clock, FileText, AlertCircle } from 'lucide-react';
import { Modal, Button, Badge } from '../common';
import { Category, CreateVideoData, UpdateVideoData, Video } from '../../types';

interface CreateVideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  categories: Category[];
  onSubmit: (data: CreateVideoData) => Promise<void>;
  isSubmitting: boolean;
}

interface UpdateVideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  video: Video | null;
  onSubmit: (id: string, data: UpdateVideoData) => Promise<void>;
  isSubmitting: boolean;
}

// Función para extraer ID de video de YouTube
const extractYouTubeId = (url: string): string | null => {
  const regex = /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/;
  const match = url.match(regex);
  return match ? match[1] : null;
};

// Función para validar URL de YouTube
const isValidYouTubeUrl = (url: string): boolean => {
  const regex = /^https:\/\/(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)/;
  return regex.test(url);
};

// Función para obtener thumbnail de YouTube
const getYouTubeThumbnail = (url: string): string => {
  const videoId = extractYouTubeId(url);
  return videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : '';
};

// Modal para crear video
export const CreateVideoModal: React.FC<CreateVideoModalProps> = ({
  isOpen,
  onClose,
  categories,
  onSubmit,
  isSubmitting
}) => {
  const [formData, setFormData] = useState({
    categoryId: '',
    title: '',
    youtubeUrl: '',
    description: '',
    duration: ''
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [previewUrl, setPreviewUrl] = useState('');

  // Reset form cuando se abre/cierra
  useEffect(() => {
    if (isOpen) {
      setFormData({
        categoryId: '',
        title: '',
        youtubeUrl: '',
        description: '',
        duration: ''
      });
      setErrors({});
      setPreviewUrl('');
    }
  }, [isOpen]);

  // Actualizar preview cuando cambia la URL
  useEffect(() => {
    if (formData.youtubeUrl && isValidYouTubeUrl(formData.youtubeUrl)) {
      setPreviewUrl(getYouTubeThumbnail(formData.youtubeUrl));
      setErrors(prev => ({ ...prev, youtubeUrl: '' }));
    } else if (formData.youtubeUrl) {
      setPreviewUrl('');
      setErrors(prev => ({ ...prev, youtubeUrl: 'URL de YouTube no válida' }));
    } else {
      setPreviewUrl('');
      setErrors(prev => ({ ...prev, youtubeUrl: '' }));
    }
  }, [formData.youtubeUrl]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validación
    const newErrors: Record<string, string> = {};
    
    if (!formData.categoryId) newErrors.categoryId = 'Categoría requerida';
    if (!formData.title) newErrors.title = 'Título requerido';
    if (!formData.youtubeUrl) {
      newErrors.youtubeUrl = 'URL de YouTube requerida';
    } else if (!isValidYouTubeUrl(formData.youtubeUrl)) {
      newErrors.youtubeUrl = 'URL de YouTube no válida';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const submitData: CreateVideoData = {
        categoryId: formData.categoryId,
        title: formData.title,
        youtubeUrl: formData.youtubeUrl,
        description: formData.description || undefined,
        duration: formData.duration ? parseInt(formData.duration) : undefined
      };

      await onSubmit(submitData);
      onClose();
    } catch (error) {
      // El error se maneja en el hook
    }
  };

  const selectedCategory = categories.find(c => c.id === formData.categoryId);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Crear Video" size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Selección de categoría */}
        <div>
          <label className="block text-sm font-medium text-brinsa-azul mb-2">
            Categoría *
          </label>
          <select
            value={formData.categoryId}
            onChange={(e) => setFormData(prev => ({ ...prev, categoryId: e.target.value }))}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brinsa-azul ${
              errors.categoryId ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value="">Seleccionar categoría...</option>
            {categories.map(category => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
          {errors.categoryId && (
            <p className="text-red-600 text-sm mt-1">{errors.categoryId}</p>
          )}
        </div>

        {/* Título */}
        <div>
          <label className="block text-sm font-medium text-brinsa-azul mb-2">
            Título del Video *
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brinsa-azul ${
              errors.title ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Introduce el título del video..."
          />
          {errors.title && (
            <p className="text-red-600 text-sm mt-1">{errors.title}</p>
          )}
        </div>

        {/* URL de YouTube */}
        <div>
          <label className="block text-sm font-medium text-brinsa-azul mb-2">
            URL de YouTube *
          </label>
          <div className="relative">
            <input
              type="url"
              value={formData.youtubeUrl}
              onChange={(e) => setFormData(prev => ({ ...prev, youtubeUrl: e.target.value }))}
              className={`w-full px-3 py-2 pl-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brinsa-azul ${
                errors.youtubeUrl ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="https://youtu.be/... o https://youtube.com/watch?v=..."
            />
            <Link className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-brinsa-azul" />
          </div>
          {errors.youtubeUrl && (
            <p className="text-red-600 text-sm mt-1">{errors.youtubeUrl}</p>
          )}
          
          {/* Preview del video */}
          {previewUrl && (
            <div className="mt-3">
              <div className="relative w-full max-w-sm">
                <img
                  src={previewUrl}
                  alt="Preview del video"
                  className="w-full h-32 object-cover rounded-lg border border-brinsa-celeste/30"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center">
                    <Play className="w-6 h-6 text-white ml-1" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Descripción */}
        <div>
          <label className="block text-sm font-medium text-brinsa-azul mb-2">
            Descripción
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brinsa-azul"
            placeholder="Descripción opcional del video..."
          />
        </div>

        {/* Duración */}
        <div>
          <label className="block text-sm font-medium text-brinsa-azul mb-2">
            Duración (segundos)
          </label>
          <div className="relative">
            <input
              type="number"
              min="1"
              value={formData.duration}
              onChange={(e) => setFormData(prev => ({ ...prev, duration: e.target.value }))}
              className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brinsa-azul"
              placeholder="Duración en segundos (opcional)..."
            />
            <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-brinsa-azul" />
          </div>
        </div>

        {/* Información adicional */}
        {selectedCategory && (
          <div className="bg-brinsa-celeste/10 border border-brinsa-celeste/30 rounded-lg p-4">
            <div className="flex items-center mb-2">
              <FileText className="w-4 h-4 text-brinsa-azul mr-2" />
              <span className="text-sm font-medium text-brinsa-azul">Información</span>
            </div>
            <p className="text-sm text-brinsa-azulOscuro/80">
              El video se creará en la categoría <Badge variant="default" size="sm">{selectedCategory.name}</Badge>
            </p>
          </div>
        )}

        {/* Botones */}
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Creando...' : 'Crear Video'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

// Modal para actualizar video
export const UpdateVideoModal: React.FC<UpdateVideoModalProps> = ({
  isOpen,
  onClose,
  video,
  onSubmit,
  isSubmitting
}) => {
  const [formData, setFormData] = useState({
    title: '',
    youtubeUrl: '',
    description: '',
    duration: ''
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [previewUrl, setPreviewUrl] = useState('');

  // Cargar datos del video cuando se abre el modal
  useEffect(() => {
    if (isOpen && video) {
      setFormData({
        title: video.title,
        youtubeUrl: video.youtubeUrl,
        description: video.description || '',
        duration: video.duration ? video.duration.toString() : ''
      });
      setErrors({});
      setPreviewUrl(getYouTubeThumbnail(video.youtubeUrl));
    }
  }, [isOpen, video]);

  // Actualizar preview cuando cambia la URL
  useEffect(() => {
    if (formData.youtubeUrl && isValidYouTubeUrl(formData.youtubeUrl)) {
      setPreviewUrl(getYouTubeThumbnail(formData.youtubeUrl));
      setErrors(prev => ({ ...prev, youtubeUrl: '' }));
    } else if (formData.youtubeUrl) {
      setPreviewUrl('');
      setErrors(prev => ({ ...prev, youtubeUrl: 'URL de YouTube no válida' }));
    } else {
      setPreviewUrl('');
      setErrors(prev => ({ ...prev, youtubeUrl: '' }));
    }
  }, [formData.youtubeUrl]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!video) return;

    // Validación
    const newErrors: Record<string, string> = {};
    
    if (!formData.title) newErrors.title = 'Título requerido';
    if (!formData.youtubeUrl) {
      newErrors.youtubeUrl = 'URL de YouTube requerida';
    } else if (!isValidYouTubeUrl(formData.youtubeUrl)) {
      newErrors.youtubeUrl = 'URL de YouTube no válida';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const submitData: UpdateVideoData = {
        title: formData.title,
        youtubeUrl: formData.youtubeUrl,
        description: formData.description || undefined,
        duration: formData.duration ? parseInt(formData.duration) : undefined
      };

      await onSubmit(video.id, submitData);
      onClose();
    } catch (error) {
      // El error se maneja en el hook
    }
  };

  if (!video) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Actualizar Video" size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Información de la categoría */}
        <div className="bg-brinsa-celeste/10 border border-brinsa-celeste/30 rounded-lg p-4">
          <div className="flex items-center mb-2">
            <FileText className="w-4 h-4 text-brinsa-azul mr-2" />
            <span className="text-sm font-medium text-brinsa-azul">Categoría</span>
          </div>
          <p className="text-sm text-brinsa-azulOscuro/80">
            Video de la categoría <Badge variant="default" size="sm">{video.category?.name}</Badge>
          </p>
        </div>

        {/* Título */}
        <div>
          <label className="block text-sm font-medium text-brinsa-azul mb-2">
            Título del Video *
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brinsa-azul ${
              errors.title ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Introduce el título del video..."
          />
          {errors.title && (
            <p className="text-red-600 text-sm mt-1">{errors.title}</p>
          )}
        </div>

        {/* URL de YouTube */}
        <div>
          <label className="block text-sm font-medium text-brinsa-azul mb-2">
            URL de YouTube *
          </label>
          <div className="relative">
            <input
              type="url"
              value={formData.youtubeUrl}
              onChange={(e) => setFormData(prev => ({ ...prev, youtubeUrl: e.target.value }))}
              className={`w-full px-3 py-2 pl-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brinsa-azul ${
                errors.youtubeUrl ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="https://youtu.be/... o https://youtube.com/watch?v=..."
            />
            <Link className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-brinsa-azul" />
          </div>
          {errors.youtubeUrl && (
            <p className="text-red-600 text-sm mt-1">{errors.youtubeUrl}</p>
          )}
          
          {/* Preview del video */}
          {previewUrl && (
            <div className="mt-3">
              <div className="relative w-full max-w-sm">
                <img
                  src={previewUrl}
                  alt="Preview del video"
                  className="w-full h-32 object-cover rounded-lg border border-brinsa-celeste/30"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center">
                    <Play className="w-6 h-6 text-white ml-1" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Descripción */}
        <div>
          <label className="block text-sm font-medium text-brinsa-azul mb-2">
            Descripción
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brinsa-azul"
            placeholder="Descripción opcional del video..."
          />
        </div>

        {/* Duración */}
        <div>
          <label className="block text-sm font-medium text-brinsa-azul mb-2">
            Duración (segundos)
          </label>
          <div className="relative">
            <input
              type="number"
              min="1"
              value={formData.duration}
              onChange={(e) => setFormData(prev => ({ ...prev, duration: e.target.value }))}
              className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brinsa-azul"
              placeholder="Duración en segundos (opcional)..."
            />
            <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-brinsa-azul" />
          </div>
        </div>

        {/* Botones */}
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Actualizando...' : 'Actualizar Video'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};